/**
 * A página do arquivo de um documento do caso — o destino do link impresso em cada
 * referência do documento emitido (E93.F4, GUS-56).
 *
 * Fica fora das áreas do perito e do portal: o mesmo link circula entre o
 * escritório, o banco e a plataforma, e quem decide se o arquivo desce é o
 * recorte do acervo no servidor, não a área em que a pessoa mora. Sem sessão, o
 * `AuthGuard` leva ao login guardando este endereço, e o login volta para cá.
 *
 * Ler o registro não é baixar: o nome, o código e o tamanho aparecem logo, e
 * os bytes só descem no clique — é o clique que a trilha registra.
 */
import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';

import { Blueprint, Button, MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  baixarArquivoDaPeca,
  lerPecaDoCaso,
  motivoDaRecusa,
  type MotivoDaRecusa,
} from '@/lib/arquivo_da_peca';
import { TROCA_DE_SENHA_ROUTE } from '@/lib/auth/homeRoute';
import { formatarRecebimento, type PecaCustodiada, type TipoPeca } from '@/lib/cadeia_custodia';
import { salvarArquivoBaixado } from '@/lib/http_download';
import { formatBytes } from '@/lib/masks';
import { useTrocaDeCaso } from '@/pages/useTrocaDeCaso';

const t = ptBR.arquivoDaPeca;
const tc = ptBR.cadeiaCustodia;

const ROTULO_DO_TIPO: Record<TipoPeca, string> = {
  documento: tc.tipoDocumento,
  assinatura: tc.tipoAssinatura,
  foto: tc.tipoFoto,
};

const MENSAGEM_DA_RECUSA: Record<MotivoDaRecusa, string> = {
  fora_do_recorte: t.foraDoRecorte,
  senha_provisoria: t.senhaProvisoria,
  nao_encontrado: t.naoEncontrado,
  nao_confere: t.naoConfere,
  rede: t.erroRede,
  desconhecido: t.erroDesconhecido,
};

/** Recusa que muda com o tempo merece o botão de tentar de novo; a de acesso, não. */
const PASSAGEIRA: ReadonlySet<MotivoDaRecusa> = new Set(['rede', 'desconhecido']);

type Leitura =
  | { fase: 'carregando' }
  | { fase: 'pronta'; peca: PecaCustodiada }
  | { fase: 'recusada'; motivo: MotivoDaRecusa };

type Download =
  | { fase: 'parado' }
  | { fase: 'baixando' }
  | { fase: 'baixado'; codigo: string | null }
  | { fase: 'recusado'; motivo: MotivoDaRecusa };

export function ArquivoDaPecaPage() {
  const { caseId = '', pecaId = '' } = useParams();
  const location = useLocation();
  const apiClient = useApiClient();
  const chave = `${caseId}/${pecaId}`;
  const [leitura, setLeitura] = useState<Leitura>({ fase: 'carregando' });
  const [download, setDownload] = useState<Download>({ fase: 'parado' });
  const [tentativa, setTentativa] = useState(0);
  // Outro link aberto na mesma montagem: nada do arquivo anterior fica na tela.
  const vigente = useTrocaDeCaso(chave, () => {
    setLeitura({ fase: 'carregando' });
    setDownload({ fase: 'parado' });
  });

  useEffect(() => {
    const alvo = `${caseId}/${pecaId}`;
    lerPecaDoCaso(apiClient, caseId, pecaId)
      .then((peca) => {
        if (vigente.current === alvo) setLeitura({ fase: 'pronta', peca });
      })
      .catch((err: unknown) => {
        if (vigente.current === alvo) setLeitura({ fase: 'recusada', motivo: motivoDaRecusa(err) });
      });
  }, [apiClient, caseId, pecaId, tentativa, vigente]);

  async function baixar(peca: PecaCustodiada) {
    const alvo = chave;
    setDownload({ fase: 'baixando' });
    try {
      const arquivo = await baixarArquivoDaPeca(apiClient, caseId, pecaId, peca.descricao);
      if (vigente.current !== alvo) return;
      salvarArquivoBaixado(arquivo);
      setDownload({ fase: 'baixado', codigo: arquivo.codigoVerificacao ?? null });
    } catch (err) {
      if (vigente.current === alvo) setDownload({ fase: 'recusado', motivo: motivoDaRecusa(err) });
    }
  }

  function tentarDeNovo() {
    setLeitura({ fase: 'carregando' });
    setTentativa((n) => n + 1);
  }

  // Conta com a senha provisória: o servidor recusa tudo até a troca. A troca
  // volta a este endereço, e o arquivo abre sem a pessoa procurar o link de novo.
  if (leitura.fase === 'recusada' && leitura.motivo === 'senha_provisoria') {
    return <Navigate to={TROCA_DE_SENHA_ROUTE} replace state={{ from: location.pathname }} />;
  }

  return (
    <Blueprint
      as="section"
      data-testid="arquivo-da-peca-page"
      aria-labelledby="arquivo-da-peca-title"
      className="bg-bg px-8 py-[34px]"
    >
      <p className="mb-2.5 font-titulo text-rotulo uppercase text-fg-dim">{t.eyebrow}</p>
      {leitura.fase === 'carregando' ? (
        <p
          role="status"
          data-testid="arquivo-da-peca-carregando"
          className="text-body-sm text-fg-muted"
        >
          {t.carregando}
        </p>
      ) : null}
      {leitura.fase === 'recusada' ? (
        <Recusa motivo={leitura.motivo} onTentarDeNovo={tentarDeNovo} />
      ) : null}
      {leitura.fase === 'pronta' ? (
        <Registro
          peca={leitura.peca}
          download={download}
          onBaixar={() => void baixar(leitura.peca)}
        />
      ) : null}
    </Blueprint>
  );
}

function Recusa({
  motivo,
  onTentarDeNovo,
}: {
  motivo: MotivoDaRecusa;
  onTentarDeNovo: () => void;
}) {
  return (
    <>
      <h1 id="arquivo-da-peca-title" className="font-display text-[32px] leading-none text-fg">
        {t.recusaTitulo}
      </h1>
      <p
        role="alert"
        data-testid={`arquivo-da-peca-recusa-${motivo}`}
        className="mt-3 text-body-sm text-fg"
      >
        {MENSAGEM_DA_RECUSA[motivo]}
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-4">
        {PASSAGEIRA.has(motivo) ? (
          <Button type="button" onClick={onTentarDeNovo} data-testid="arquivo-da-peca-tentar">
            {t.tentarDeNovo}
          </Button>
        ) : null}
        <Link
          to="/"
          className="text-body-sm text-accent-700 underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-accent"
        >
          {t.irParaInicio}
        </Link>
      </div>
    </>
  );
}

interface RegistroProps {
  peca: PecaCustodiada;
  download: Download;
  onBaixar: () => void;
}

function Registro({ peca, download, onBaixar }: RegistroProps) {
  const recebido = t.recebidoPor
    .replace('{{data}}', formatarRecebimento(peca.anexado_em))
    .replace('{{autor}}', peca.anexado_por);
  return (
    <>
      <h1
        id="arquivo-da-peca-title"
        className="break-words font-display text-[32px] leading-none text-fg"
      >
        {peca.descricao}
      </h1>
      <p className="mt-1.5 text-body-sm text-fg-muted">{t.intro}</p>
      <dl className="mt-6 grid gap-x-6 gap-y-3 text-body-sm sm:grid-cols-[max-content_1fr]">
        <dt className="text-fg-dim">{t.rotuloTipo}</dt>
        <dd>{ROTULO_DO_TIPO[peca.tipo]}</dd>
        <dt className="text-fg-dim">{t.rotuloCodigo}</dt>
        {peca.sha256 ? (
          <MonoData as="dd" data-testid="arquivo-da-peca-codigo" className="break-all">
            {peca.sha256}
          </MonoData>
        ) : (
          <dd data-testid="arquivo-da-peca-codigo">{tc.semCodigo}</dd>
        )}
        <dt className="text-fg-dim">{t.rotuloTamanho}</dt>
        <MonoData as="dd">{formatBytes(peca.tamanho_bytes)}</MonoData>
        <dt className="text-fg-dim">{t.rotuloRecebido}</dt>
        <dd>{recebido}</dd>
      </dl>
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <Button
          type="button"
          onClick={onBaixar}
          disabled={download.fase === 'baixando'}
          data-testid="arquivo-da-peca-baixar"
        >
          {download.fase === 'baixando' ? t.baixando : t.baixar}
        </Button>
      </div>
      <Resultado download={download} />
    </>
  );
}

function Resultado({ download }: { download: Download }) {
  if (download.fase === 'recusado') {
    return (
      <p
        role="alert"
        data-testid={`arquivo-da-peca-recusa-${download.motivo}`}
        className="mt-4 text-body-sm text-fg"
      >
        {MENSAGEM_DA_RECUSA[download.motivo]}
      </p>
    );
  }
  if (download.fase !== 'baixado') return null;
  return (
    <div role="status" data-testid="arquivo-da-peca-baixado" className="mt-4 text-body-sm text-fg">
      <p>{t.baixado}</p>
      {download.codigo ? (
        <p className="mt-1 text-fg-muted">
          {t.codigoDoBaixado} <MonoData className="break-all">{download.codigo}</MonoData>
        </p>
      ) : null}
    </div>
  );
}
